import { useDebounceFn, useLatest } from 'ahooks';
import { isNil, isObject } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

export type ValidateResult = (string | ({ isValid: boolean; message?: string }) | undefined | true)

/**
 * 验证器函数类型
 * @template T 验证值的类型
 * @param value 要验证的值
 * @returns 验证结果：
 *   - undefined | true | '' | { isValid: true } 表示验证通过
 *   - string 表示验证失败，返回错误信息
 *   - { isValid: false, message: string } 表示验证失败，返回详细错误信息
 */
export type Validator<T = unknown> = (value: T) => ValidateResult

/**
 * 验证属性类型，用于传递给表单组件
 */
export type ValidateProps = {
  /** 验证状态，error 表示验证失败 */
  status?: 'error';
  /** 错误提示信息 */
  helpMsg?: string;
  /** 失焦时触发验证的回调函数 */
  onBlur: () => void;
}

const getPropByValidateResult = (result: ValidateResult): Omit<ValidateProps, 'onBlur'> => {
  let validateProps: Omit<ValidateProps, 'onBlur'> = {};

  if (isObject(result) && result.isValid === false) {
    validateProps = {
      status: 'error',
      helpMsg: result.message,
    };
  } else if (typeof result === 'string' && result !== '') {
    validateProps = {
      status: 'error',
      helpMsg: result,
    };
  }
  return validateProps;
};

/**
 * 表单验证 Hook
 *
 * 提供防抖验证功能，在用户失焦300m后，异步触发验证。value改变清空验证结果，等下次失焦时触发验证。why 300ms？大多数动画的取值时间是300ms。
 *
 * @template T 验证值的类型
 * @param value 要验证的值
 * @param rules 验证规则，可以是单个验证器或验证器数组
 * @returns 返回包含验证属性的对象，可直接传递给表单组件
 *
 * @example
 * ```tsx
 * // 基础用法
 * const [email, setEmail] = useState('');
 *
 * const { validateProps } = useValidateProps(email, (value: string) => {
 *   if (!value) return '邮箱不能为空';
 *   if (!/\S+@\S+\.\S+/.test(value)) return '邮箱格式不正确';
 *   return true;
 * });
 *
 * return (
 *   <Input
 *     value={email}
 *     onChange={(e) => setEmail(e.target.value)}
 *     placeholder="请输入邮箱"
 *     {...validateProps}
 *   />
 * );
 * ```
 *
 * @example
 * ```tsx
 * // 手动触发
 * const [password, setPassword] = useState('');
 * const passwordValidators = [
 *   (value: string) => !value ? '密码不能为空' : true,
 *   (value: string) => value.length < 6 ? '密码长度不能少于6位' : true,
 *   (value: string) => !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)
 *     ? '密码必须包含大小写字母和数字' : true,
 * ];
 *
 * const { validateProps, trigValidate, reset } = useValidateProps(password, passwordValidators);
 * const result = trigValidate(); // 手动触发验证并获取结果
 * const result2 = trigValidate(true); // 只验证，不更新状态
 * reset(); // 重置验证状态
 *
 * ```
 */
export function useValidateHelper<T = unknown>(value: T, rules: Validator<T> | Validator<T>[]) {
  const [validateProps, setValidateProps] = useState<Omit<ValidateProps, 'onBlur'>>({});
  const rulesRef = useLatest(Array.isArray(rules) ? rules : [rules]);

  const reset = useCallback(() => setValidateProps({}), []);
  const updateValidateResult = useCallback((ret: ValidateResult) => {
    setValidateProps(getPropByValidateResult(ret));
  }, []);

  const validate = useCallback((justValidate = false) => {
    let validateProps: Omit<ValidateProps, 'onBlur'> = {};
    let result: ValidateResult;
    const trueRules = rulesRef.current || [];
    for (const rule of trueRules) {
      result = rule(value);
      validateProps = getPropByValidateResult(result);

      if (validateProps.status === 'error') break;
    }
    if (!justValidate) {
      setValidateProps(validateProps);
    }
    return result;
  }, [rulesRef, value]);

  const onBlur = useDebounceFn(() => validate(), { wait: 300, leading: false, trailing: true });

  useEffect(() => {
    setValidateProps({});
  }, [value]);

  return {
    reset,
    updateValidateResult,
    trigValidate: validate,
    validateProps: {
      ...validateProps,
      onBlur: onBlur.run,
    },
  };
}

/**
 * 是否通过校验
 * @param result 验证结果
 * @returns
 */
export function isValidResult(result: ValidateResult) {
  return result === ''
    || isNil(result)
    || result === true
    || (isObject(result) && result.isValid);
}

/**
 *  组合验证函数，用于多个验证函数的组合验证
 * @param validates 验证函数数组
 * @returns
 */
export function composeTrigValidate(...validates: Array<(justValidate?: boolean) => ValidateResult>) {
  const results = validates.map(validate => validate());

  return {
    isValid: results.every(isValidResult),
    data: results,
  };
}